import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

interface UseStorageOptions<T> {
  callbacks: Set<(key: string) => void>;
  fallbackStorage: Map<string, unknown>;
  initialValue: T;
  key: string;
  resolveValue: (newValue: SetStateAction<T>, currentValue: T) => T;
  storage: Storage;
}

function triggerCallbacks(
  callbacks: Set<(key: string) => void>,
  key: string,
): void {
  for (const callback of [...callbacks]) {
    callback(key);
  }
}

export function useStorage<T>({
  callbacks,
  fallbackStorage,
  initialValue,
  key,
  resolveValue,
  storage,
}: UseStorageOptions<T>): [T, Dispatch<SetStateAction<T>>] {
  const stringifiedInitialValue = JSON.stringify(initialValue);

  const getSnapshot = (): string | null => {
    const item = fallbackStorage.has(key)
      ? fallbackStorage.get(key)
      : storage.getItem(key);

    return item ? (item as string) : null;
  };

  const initialStorageSnapshot = useRef<string | null>(getSnapshot());
  const initialStorageValue = useRef<T>(
    initialStorageSnapshot.current
      ? (JSON.parse(initialStorageSnapshot.current as string) as T)
      : initialValue,
  );

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const onChange = (storageKey: string | null) => {
        if (storageKey === key) {
          onStoreChange();
        }
      };
      const onStorageChange = (event: StorageEvent) => {
        if (event.storageArea === storage) {
          onChange(event.key);
        }
      };

      callbacks.add(onChange);
      window.addEventListener('storage', onStorageChange);

      return () => {
        callbacks.delete(onChange);
        window.removeEventListener('storage', onStorageChange);
      };
    },
    [callbacks, key, storage],
  );

  const value = useSyncExternalStore(subscribe, getSnapshot);

  const setState = useCallback<Dispatch<SetStateAction<T>>>(
    (newValue) => {
      const value = resolveValue(newValue, initialStorageValue.current);

      try {
        initialStorageValue.current = value;
        storage.setItem(key, JSON.stringify(value));
        fallbackStorage.delete(key);
      } catch {
        fallbackStorage.set(key, JSON.stringify(value));
      }

      triggerCallbacks(callbacks, key);
    },
    [callbacks, fallbackStorage, key, resolveValue, storage],
  );

  const storedValue = useMemo(
    () => (JSON.parse(value ? value : stringifiedInitialValue) ?? '{}') as T,
    [stringifiedInitialValue, value],
  );
  const hasInvalidStoredValue =
    value !== null &&
    Array.isArray(initialValue) &&
    !Array.isArray(storedValue);

  // Remove this repair when object-shaped arrays from older releases no longer need support.
  useEffect(() => {
    if (hasInvalidStoredValue) setState(() => initialValue);
  }, [initialValue, hasInvalidStoredValue, setState]);

  const safeValue = hasInvalidStoredValue ? initialValue : storedValue;
  return useMemo(() => [safeValue, setState], [safeValue, setState]);
}
